import { ChangeDetectionStrategy, Component, inject, Input, OnInit, output } from '@angular/core';
import { InstellingETLStatus, LicentieRol, RInstelling, RInstellingAdditionalObjectKey, RJobrunInstellingPrimer } from '@cumlaude/service-contract';
import { InstellingLabelsComponent } from '../instelling-labels/instelling-labels.component';
import { CheckboxComponent } from '@cumlaude/shared-components-inputs';
import { isUndefined } from 'lodash-es';
import { addDefined, isDefined } from '@cumlaude/shared-utils';
import { DatePipe, NgClass } from '@angular/common';
import { IconComponent, type IconName, type ColorToken } from '@cumlaude/shared-components-icons';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';

interface StatusInfo {
	checked: boolean;
	disabled: boolean;
	outdatedBackup: boolean;
	instellingETLStatus: InstellingETLStatus | undefined;
	laatsteDelta: RJobrunInstellingPrimer | undefined;
	laatsteBulk: RJobrunInstellingPrimer | undefined;
}

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const BACKUP_NAME_DATE_PATTERN = /_(\d{4})_(\d{2})_(\d{2})_(\d{6})(?:_\d+)?\.bak$/i;

@Component({
	selector: 'app-instellingen-list',
	templateUrl: './instellingen-list.component.html',
	styleUrl: './instellingen-list.component.scss',
	imports: [InstellingLabelsComponent, CheckboxComponent, NgClass, DatePipe, IconComponent, TooltipDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstellingenListComponent implements OnInit {
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);

	etlIconStatussen: InstellingETLStatus[] = [InstellingETLStatus.GEPLAND, InstellingETLStatus.BEZIG, InstellingETLStatus.GEFAALD];

	readonly ETL_STATUS_ICON: Partial<Record<InstellingETLStatus, { name: IconName; color: ColorToken; title: string }>> = {
		[InstellingETLStatus.GEFAALD]: { name: 'error', color: 'error-1', title: 'Laatste ETL voor deze instelling is gefaald' },
		[InstellingETLStatus.BEZIG]: { name: 'bezig', color: 'primary-2', title: 'Nieuwe ETL voor deze instelling is bezig' },
		[InstellingETLStatus.GEPLAND]: { name: 'gepland', color: 'primary-2', title: 'Nieuwe ETL voor deze instelling is gepland' },
	};

	@Input()
	instellingen!: RInstelling[];

	@Input()
	checked?: RInstelling[];

	@Input()
	disabled?: RInstelling[];

	@Input()
	warningIconEnabled = false;

	@Input()
	statusEnabled = false;

	@Input()
	etlStatusEnabled = false;

	@Input()
	toonBestuurLabel = true;

	@Input()
	licenties: LicentieRol[] = [];

	@Input()
	totaalEnabled = false;

	checkboxEnabled = false;

	instellingClick = output<RInstelling>();

	instellingCtrlClick = output<RInstelling>();

	ngOnInit() {
		this.checkboxEnabled = !isUndefined(this.checked);
	}

	getStatus(instelling: RInstelling): StatusInfo {
		return {
			disabled: this.disabled?.some((target) => target.id === instelling.id) ?? false,
			checked: this.checked?.some((target) => target.id === instelling.id) ?? false,
			outdatedBackup: instelling.actief && this.warningIconEnabled ? this.verifyOutdatedBackup(instelling.backupName) : false,
			instellingETLStatus: instelling.actief && this.warningIconEnabled ? instelling.instellingETLStatus : undefined,
			laatsteDelta: instelling.actief && this.etlStatusEnabled ? instelling.laatsteDeltaJobrun : undefined,
			laatsteBulk: instelling.actief && this.etlStatusEnabled ? instelling.laatsteBulkJobrun : undefined,
		};
	}

	private verifyOutdatedBackup(backupName?: string) {
		if (!isDefined(backupName)) return false;

		const match = BACKUP_NAME_DATE_PATTERN.exec(backupName);
		if (match === null) return false;

		const [, year, month, day, time] = match;
		const hours = Number(time.slice(0, 2));
		const minutes = Number(time.slice(2, 4));
		const seconds = Number(time.slice(4, 6));

		const backupDate = new Date(Number(year), Number(month) - 1, Number(day), hours, minutes, seconds);
		if (Number.isNaN(backupDate.getTime())) return false;

		let outdatedAfter = ONE_DAY_IN_MS;
		if (this.envConfig.environmentName !== 'Production') outdatedAfter = outdatedAfter * 2;

		return Date.now() - backupDate.getTime() > outdatedAfter;
	}

	getLicentieRolLabel(licentieRol: LicentieRol): string {
		const map = {
			[LicentieRol.SCHOOLBREEDTEAM]: 'School',
			[LicentieRol.SECTIE]: 'Sectie',
			[LicentieRol.DOCENTMENTOR]: 'Docent',
			[LicentieRol.AANWEZIGHEID]: 'Aanwezigheid',
			[LicentieRol.BESTUUR]: 'Bestuur',
			[LicentieRol.BESTUURTOTAAL]: 'Bestuur (totaal)',
		};
		return map[licentieRol];
	}

	getLicenties(instelling: RInstelling, rol: LicentieRol): string | undefined {
		const data = instelling.additionalObjects![RInstellingAdditionalObjectKey.LICENTIES]?.[rol];
		if (!isDefined(data)) return undefined;

		if (isDefined(data.totaal) && ![LicentieRol.BESTUUR, LicentieRol.DOCENTMENTOR, LicentieRol.AANWEZIGHEID].includes(rol))
			return `${data.inGebruik} / ${data.totaal}`;

		return `${data.inGebruik}`;
	}

	getTotaalLicenties(rol: LicentieRol) {
		const [inGebruik, totaal] = this.instellingen
			.map((instelling) => {
				const data = instelling.additionalObjects![RInstellingAdditionalObjectKey.LICENTIES]?.[rol];
				return [data?.inGebruik, data?.totaal];
			})
			.reduce(([a_g, a_t], [b_g, b_t]) => [addDefined(a_g, b_g), addDefined(a_t, b_t)], [undefined, undefined]);

		if (isDefined(totaal) && ![LicentieRol.BESTUUR, LicentieRol.DOCENTMENTOR, LicentieRol.AANWEZIGHEID].includes(rol))
			return `${inGebruik} / ${totaal}`;

		return `${inGebruik ?? 0}`;
	}

	protected readonly InstellingETLStatus = InstellingETLStatus;
	protected readonly LicentieRol = LicentieRol;
	protected readonly RInstellingAdditionalObjectKey = RInstellingAdditionalObjectKey;

	protected click(event: MouseEvent, instelling: RInstelling) {
		if (event.ctrlKey || event.metaKey) this.instellingCtrlClick.emit(instelling);
		else this.instellingClick.emit(instelling);
	}
}
